// Build tracker (signed language). Jacky, 25 October 2026: "each CRM feature navigation page is a feature, it
// show history of all change and improvement, and bug fix … any code change affecting the x page, must register
// in simple human language, whats changed, or fixed, or added."
//
// Left: the features (one per rail page, plus the other faces) with counts. Right: the chosen feature's history,
// newest first — date, kind, the sentence in the CRM's language, the pull request. The rows are crm.build_entry,
// a copy of build-tracker/entries/*.json in the repo (CI refuses a code change to a page without its entry).
// Internal entries (nothing a person sees changed) are folded away until asked for.
import {useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Page} from '../shell/Page';
import {cn} from '@aiw/ui';
import {Tag} from '../components/aligned-table';
import {useT} from '../lib/i18n';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {day} from '../lib/format';
import FEATURES from '../../../../build-tracker/features.json';

type Kind = 'added' | 'changed' | 'fixed' | 'internal';
interface Entry { id: string; features: string[]; kind: Kind; summary_en: string; summary_zh: string; pr: number | null; sha: string | null; happened_on: string; source: 'entry' | 'backfill' }
type Feature = { route: string | null; en: string; zh: string };

const FEATURE_LIST = Object.entries((FEATURES as {features: Record<string, Feature>}).features);
const REPO = 'https://github.com/artemis-group/artemis-intelligent-web';
const TONE: Record<Kind, 'ok' | 'info' | 'warn' | undefined> = {added: 'ok', changed: 'info', fixed: 'warn', internal: undefined};

async function get<T>(path: string): Promise<T> {
  const r = await fetchImpl(`${env.apiBase}${path}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Unavailable');
  return body as T;
}

export function BuildTracker() {
  const {t, lang} = useT();
  const [feature, setFeature] = useState<string | null>(null);
  const [internal, setInternal] = useState(false);
  const [q, setQ] = useState('');
  const list = useQuery({queryKey: ['build-tracker'], queryFn: () => get<{data: Entry[]}>('/api/build-tracker'), staleTime: 60_000});
  const all = list.data?.data ?? [];
  const counts = useMemo(() => { const c = new Map<string, number>(); for (const e of all) if (e.kind !== 'internal') for (const f of e.features) c.set(f, (c.get(f) ?? 0) + 1); return c; }, [all]);
  const text = (e: Entry) => (lang === 'zh' ? e.summary_zh : e.summary_en);
  const label = (key: string) => { const f = (FEATURES as {features: Record<string, Feature>}).features[key]; return f ? (lang === 'zh' ? f.zh : f.en) : key; };
  const needle = q.trim().toLowerCase();
  const rows = all.filter((e) => (feature ? e.features.includes(feature) : true) && (internal || e.kind !== 'internal') && (!needle || text(e).toLowerCase().includes(needle) || e.summary_en.toLowerCase().includes(needle) || String(e.pr ?? '').includes(needle)));
  const visibleTotal = all.filter((e) => e.kind !== 'internal').length;

  return <Page kicker={t('bt.kicker')} title={t('bt.title')} lede={t('bt.lede')}
    actions={<>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('bt.search')} aria-label={t('bt.search')} className="h-8 w-48 rounded-md border bg-card px-2 text-sm" />
      <button type="button" onClick={() => setInternal((v) => !v)} className="h-8 rounded-md border px-2 text-sm text-muted-foreground hover:bg-muted">{internal ? t('bt.hideInternal') : t('bt.showInternal')}</button>
    </>}>
    <div className="grid gap-4 md:grid-cols-[14rem_1fr]">
      <nav aria-label={t('bt.feature')} className="rounded-lg border bg-card p-1 text-sm md:sticky md:top-20 md:self-start">
        <FeatureButton active={feature === null} onClick={() => setFeature(null)} name={t('bt.all')} count={visibleTotal} />
        {FEATURE_LIST.map(([key, f]) => <FeatureButton key={key} active={feature === key} onClick={() => setFeature(key)} name={lang === 'zh' ? f.zh : f.en} count={counts.get(key) ?? 0} />)}
      </nav>
      <section className="min-w-0">
        {list.isError && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">{t('bt.unavailable')}</p>}
        {list.isLoading && <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}</div>}
        {list.isSuccess && rows.length === 0 && <p className="rounded-lg border p-4 text-sm text-muted-foreground">{t('bt.empty')}</p>}
        {rows.length > 0 && <ol className="divide-y rounded-lg border bg-card">
          {rows.map((e) => <li key={e.id} className="grid gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[6.5rem_4.5rem_1fr]">
            <time dateTime={e.happened_on} className="num text-micro text-muted-foreground sm:pt-0.5">{day(e.happened_on)}</time>
            <div><Tag tone={TONE[e.kind]}>{t(`bt.kind.${e.kind}` as const)}</Tag></div>
            <div className="min-w-0 text-sm">
              <p>{text(e)}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-micro text-muted-foreground">
                {feature === null && e.features.map((f) => <button key={f} type="button" onClick={() => setFeature(f)} className="underline-offset-2 hover:underline">{label(f)}</button>)}
                {e.pr !== null && <a href={`${REPO}/pull/${e.pr}`} target="_blank" rel="noreferrer" className="font-mono hover:underline">{t('bt.pr', {pr: e.pr})}</a>}
                {e.source === 'backfill' && <span>· {t('bt.backfill')}</span>}
              </p>
            </div>
          </li>)}
        </ol>}
      </section>
    </div>
  </Page>;
}

function FeatureButton({active, onClick, name, count}: {active: boolean; onClick: () => void; name: string; count: number}) {
  return <button type="button" onClick={onClick} aria-current={active ? 'page' : undefined}
    className={cn('flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left hover:bg-muted', active && 'bg-muted font-medium')}>
    <span className="truncate">{name}</span>
    <span className="num text-micro text-muted-foreground">{count || ''}</span>
  </button>;
}
